import {useQuery} from "@apollo/client";
import {Helmet} from "react-helmet-async";
import {useSearchParams} from "react-router";

import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last30Days} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {useDebouncedSearch} from "@/lib/hooks/useDebouncedSearch.ts";
import {GQL_SLA_REPORT_VIEW, SLAReportViewResponse} from "@/lib/queries/reports.ts";
import {SLAReportActionBar} from "@/pages/reports/sla/details/SLAReportActionBar.tsx";
import {SLAReportDetailsEmpty} from "@/pages/reports/sla/details/SLAReportDetailsEmpty.tsx";
import {SLAReportOverview} from "@/pages/reports/sla/details/SLAReportOverview.tsx";
import {SLAReportsOverviewActionBar} from "@/pages/reports/sla/details/SLAReportsOverviewActionBar.tsx";

export const SLA_QUERY_PARAM = "search";
const START_PARAM = "start";
const END_PARAM = "end";
const FIXED_RANGE_PARAM = "range";
const GROUPS_PARAM = "no_group";

interface SLAReportDetailsContentProps {
  id: number;
}

export const SLAReportDetailsContent = ({id}: SLAReportDetailsContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {search, searchDebounced, searchChanged} = useDebouncedSearch({searchKey: SLA_QUERY_PARAM});
  const skipGroups = !!searchParams.get(GROUPS_PARAM) || false;
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: START_PARAM,
    toKey: END_PARAM,
    fixedRangeKey: FIXED_RANGE_PARAM,
    withDefault: Last30Days,
    withReportRangeOptions: true,
  });

  const {data, previousData, error} = useQuery<SLAReportViewResponse>(GQL_SLA_REPORT_VIEW, {
    variables: {
      id: id,
      start: dateRange.from.toISOString(),
      end: dateRange.to.toISOString(),
      query: searchDebounced,
      skipGroup: skipGroups,
    },
  });

  if (error) {
    console.error(error.message);
  }

  const renderData = data || previousData;

  if (!renderData) {
    return <SLAReportDetailsEmpty />;
  }

  const {
    slaReport,
    services: {items, uptime_totals, response_time, groups, hasGroups, uses_daily_response_time_data},
  } = renderData;

  return (
    <ContentContainer
      title={slaReport.name}
      actionsBar={
        <SLAReportActionBar
          query={searchDebounced}
          id={id}
          start={dateRange.from}
          end={dateRange.to}
          hasGroups={hasGroups}
        />
      }
    >
      <Helmet>
        <title>{slaReport.name}</title>
      </Helmet>
      <SLAReportsOverviewActionBar
        setQuery={searchChanged}
        servicesOnly={skipGroups}
        onRangeChange={(from, to, fixedRange) => {
          handleRangeChange(from && to ? {from: from!, to: to!} : null, fixedRange);
        }}
        setServicesOnly={(skip) => {
          const params = new URLSearchParams(searchParams);
          if (skip) {
            params.set(GROUPS_PARAM, "1");
          } else {
            params.delete(GROUPS_PARAM);
          }
          setSearchParams(params);
        }}
        hasGroups={hasGroups}
        query={search}
        start={dateRange.from}
        end={dateRange.to}
        fixedRange={fixedRangeOption?.value}
      />
      <SLAReportOverview
        responseTime={response_time}
        dailyResponseData={uses_daily_response_time_data}
        report={slaReport}
        totals={uptime_totals}
        items={items}
        groups={groups}
        groupOnly={!skipGroups}
      />
    </ContentContainer>
  );
};
